import { useEffect, useState } from "react";

/**
 * Live pricing / entitlements from the app.
 *
 * The app owns the caps and meter definitions. Marketing MUST NOT keep its
 * own copy of the numbers — that is the drift that shipped "priority
 * processing" as an imaginary Pro benefit. This hook reads the single
 * source of truth and, if unreachable, degrades to a labelled last-known
 * snapshot so nothing is presented as authoritative that isn't.
 *
 * Contract with the app team:
 *   GET https://work.voxiumscript.com/api/entitlements
 *   - public, unauthenticated
 *   - ETag + Cache-Control expected
 *   - shape: at minimum `{ tiers: { <slug>: { price, cadence?, annualPrice?, ... } } }`
 *   - additional fields (caps, meter labels, overage-eligibility flags, etc.)
 *     are pass-through — the site reads by well-known keys and falls back
 *     per-field on missing values.
 *
 * As of the last verified probe (see report), the endpoint returns HTTP 404.
 * The site therefore renders the FALLBACK below with a visible staleness
 * banner. When the endpoint goes live no code change is needed on this side.
 */

/**
 * Endpoint configuration.
 *
 * Defaults to the PRODUCTION host, because that is what real users hit and
 * therefore what must be shown to work (today: it 404s, so the failure path
 * runs). This is the ONLY value the hook reads. No runtime override.
 */
export const DEFAULT_ENTITLEMENTS_URL =
  "https://work.voxiumscript.com/api/entitlements";

const resolveEndpoint = () => DEFAULT_ENTITLEMENTS_URL;

// Legacy export kept for callers that only need the default.
export const ENTITLEMENTS_ENDPOINT = DEFAULT_ENTITLEMENTS_URL;

const CACHE_KEY = "voxium-entitlements-v1";

/**
 * Last-known catalog. Prices only — meter numbers are intentionally omitted
 * so no cap can be misrepresented as current when the endpoint is dark.
 * The tiered UI uses qualitative wording anywhere numbers would otherwise
 * appear; only prices are treated as stable enough to survive endpoint
 * downtime.
 */
export const FALLBACK_ENTITLEMENTS = {
  tiers: {
    free: { price: 0, cadence: "lifetime" },
    standard: { price: 49, cadence: "month", annualPrice: 488 },
    pro: { price: 129, cadence: "month", annualPrice: 1290 },
    pro_plus: { price: 249, cadence: "month", annualPrice: 2490 },
    per_script: { price: 69, cadence: "one-time" },
  },
  packs: {
    qa_first_taste: { price: 4 },
    qa_sip: { price: 12 },
    qa_deep_dive: { price: 59 },
  },
  _source: "fallback",
};

const readCache = () => {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeCache = (payload) => {
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch {
    /* quota / private-mode — ignore */
  }
};

/**
 * Merge endpoint payload onto the fallback so any missing field falls back
 * to the last-known value rather than blanking the UI. Handles two known
 * `tiers` shapes: an array of tier objects with a `.key` field (the
 * currently-deployed app shape) or an object keyed by slug.
 */
const mergeOnFallback = (endpointData) => {
  const tiersByKey = Array.isArray(endpointData.tiers)
    ? Object.fromEntries(
        endpointData.tiers
          .filter((t) => t && t.key)
          .map((t) => [t.key, t])
      )
    : endpointData.tiers || {};

  const mergedTiers = {};
  for (const [slug, fbTier] of Object.entries(FALLBACK_ENTITLEMENTS.tiers)) {
    mergedTiers[slug] = { ...fbTier, ...(tiersByKey[slug] || {}) };
  }
  // Preserve any additional endpoint-only tiers (e.g., a new tier ships
  // before the fallback is updated).
  for (const [slug, endTier] of Object.entries(tiersByKey)) {
    if (!mergedTiers[slug]) mergedTiers[slug] = endTier;
  }

  return {
    ...FALLBACK_ENTITLEMENTS,
    ...endpointData,
    tiers: mergedTiers,
    _source: "endpoint",
  };
};

export const useEntitlements = () => {
  const [state, setState] = useState(() => {
    const cached = readCache();
    return {
      data: cached?.data || FALLBACK_ENTITLEMENTS,
      isStale: true,
      lastUpdated: cached?.fetchedAt || null,
      error: null,
      loading: true,
    };
  });

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const endpoint = resolveEndpoint();
      const cached = readCache();
      const headers = { Accept: "application/json" };
      if (cached?.etag) headers["If-None-Match"] = cached.etag;

      try {
        const res = await fetch(endpoint, {
          headers,
          cache: "no-store",
        });
        if (cancelled) return;

        if (res.status === 304 && cached?.data) {
          setState({
            data: cached.data,
            isStale: false,
            lastUpdated: cached.fetchedAt,
            error: null,
            loading: false,
          });
          return;
        }

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const rawData = await res.json();
        if (!rawData || typeof rawData !== "object" || !rawData.tiers) {
          throw new Error("Malformed entitlements payload");
        }

        const data = mergeOnFallback(rawData);
        const etag = res.headers.get("etag");
        const fetchedAt = new Date().toISOString();
        writeCache({ data, etag, fetchedAt });

        setState({
          data,
          isStale: false,
          lastUpdated: fetchedAt,
          error: null,
          loading: false,
        });
      } catch (err) {
        if (cancelled) return;
        // When serving cached endpoint data on a fresh failure, relabel
        // the source so the diag doesn't read "endpoint + stale" (which
        // is ambiguous). Cached data is still shown; it just carries the
        // honest label.
        const relabel = (d) =>
          d && d._source === "endpoint"
            ? { ...d, _source: "cached-endpoint" }
            : d;
        setState((prev) => ({
          data: relabel(cached?.data) || prev.data || FALLBACK_ENTITLEMENTS,
          isStale: true,
          lastUpdated: cached?.fetchedAt || null,
          error: err.message || String(err),
          loading: false,
        }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
};
